import React, { useEffect, useMemo, useRef, useState } from "react";
import { Ticket, Handbag, Crown, AlertTriangle } from "lucide-react";

// helpers
const toDateSafe = (v) => {
  if (!v) return null;

  if (typeof v?.toDate === "function") {
    try {
      const d = v.toDate();
      return isNaN(d) ? null : d;
    } catch {
      return null;
    }
  }

  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const normalizeType = (t) => {
  const s = String(t || "").toLowerCase();
  if (s === "goods") return "goods";
  if (s === "plan") return "plan";
  if (s === "reservation") return "reservation";
  return "ticket";
};

const formatRemaining = (min) => {
  const m = Math.max(0, min);
  if (m < 60) return { main: m, unit: "分" };

  const h = Math.floor(m / 60);
  const r = m % 60;
  return { main: `${h}:${String(r).padStart(2, "0")}`, unit: "" };
};

// Expired Modal
const ExpiredModal = ({ open, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
      return;
    }

    if (visible) {
      setClosing(true);
      const id = setTimeout(() => {
        setVisible(false);
        setClosing(false);
      }, 200);
      return () => clearTimeout(id);
    }
  }, [open, visible]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 200);
  };

  if (!visible) return null;

  return (
    <div
      className={[
        "fixed inset-0 z-[9999] flex items-center justify-center px-6 transition-opacity duration-200",
        closing ? "opacity-0" : "opacity-100",
      ].join(" ")}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur"
        onClick={handleClose}
      />
      <div
        className={[
          "relative w-full max-w-sm rounded-3xl bg-white shadow-2xl border overflow-hidden transition-all duration-200",
          closing ? "opacity-0 scale-95" : "opacity-100 scale-100",
        ].join(" ")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4 bg-gray-50">
          <p className="text-xs font-black tracking-widest uppercase">SESSION ENDED</p>
          <h3 className="mt-1 text-lg font-black">ご利用ありがとうございました</h3>
          <p className="mt-3 text-sm font-bold leading-relaxed">
            ご利用時間が終了しました。
            <br />
            お忘れ物がないかご確認ください。
          </p>
        </div>
        <div className="px-6 pb-6 pt-4">
          <button
            onClick={handleClose}
            className="w-full py-3 rounded-3xl font-black shadow-lg bg-gray-900 text-white"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ActiveTimeBlock
const ActiveTimeBlock = ({
  activeItems = [],
  clearActiveItem,
  className = ""
}) => {
  const [now, setNow] = useState(Date.now());
  const [expired, setExpired] = useState(null);
  const prevRef = useRef({});

  // timer
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // visible items
  const [planVisible, setPlanVisible] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const [timeVisible, setTimeVisible] = useState(false);

  const visibleItems = useMemo(() => {
    return activeItems.filter(Boolean);
  }, [activeItems]);

  const planItem = useMemo(() => {
    return visibleItems.find(i => normalizeType(i.type) === "plan");
  }, [visibleItems]);

  const prevPlanKeyRef = useRef(null);

  useEffect(() => {
    const nextKey = planItem?.activeKey || null;

    if (!nextKey) {
      prevPlanKeyRef.current = null;
      return;
    }

    if (prevPlanKeyRef.current === nextKey) return;

    prevPlanKeyRef.current = nextKey;
    setPlanVisible(true);

    const id = setTimeout(() => {
      setPlanVisible(false);
    }, 3000);

    return () => clearTimeout(id);
  }, [planItem?.activeKey]);

  // expired detection
  useEffect(() => {
    if (expired) return;

    for (const item of visibleItems) {
      const exp = toDateSafe(item.expireTime);
      if (!exp) continue;

      const prev = prevRef.current[item.activeKey];
      const diff = exp.getTime() - now;
      const curr = diff <= 0 ? 0 : Math.ceil(diff / 60000);

      prevRef.current[item.activeKey] = curr;

      if (prev > 0 && curr === 0) {
        const itemType = normalizeType(item.type);

        if (itemType === "plan") {
          clearActiveItem?.(item.activeKey);
          delete prevRef.current[item.activeKey];
          continue;
        }

        setExpired(item);
        return;
      }
    }
  }, [visibleItems, now, expired, clearActiveItem]);

  // close modal
  const handleClose = () => {
    if (!expired) return;

    const expiredType = normalizeType(expired.type);
    const expiredAtMs = toDateSafe(expired.expireTime)?.getTime() || 0;

    const keysToClear =
      expiredType === "goods"
        ? visibleItems
            .filter((item) => {
              const sameType = normalizeType(item.type) === "goods";
              const itemExpireMs = toDateSafe(item.expireTime)?.getTime() || 0;
              return sameType && itemExpireMs === expiredAtMs;
            })
            .map((item) => item.activeKey)
        : [expired.activeKey];

    keysToClear.forEach((key) => {
      clearActiveItem?.(key);
      delete prevRef.current[key];
    });

    setExpired(null);
  };

  // grouped
  const grouped = useMemo(() => {
    const map = {};

    visibleItems.forEach((i) => {
      const key = i.title;
      if (!map[key]) {
        map[key] = { title: key, quantity: 0 };
      }
      map[key].quantity += i.quantity || 1;
    });

    return Object.values(map);
  }, [visibleItems]);

  // first item
  const first = useMemo(() => {
    if (!visibleItems.length) return null;

    return [...visibleItems].sort((a, b) => {
      const ea = toDateSafe(a.expireTime)?.getTime() || 0;
      const eb = toDateSafe(b.expireTime)?.getTime() || 0;
      return eb - ea;
    })[0];
  }, [visibleItems]);

  // remaining
  const remainingMin = useMemo(() => {
    if (!visibleItems.length) return 0;

    return Math.max(
      ...visibleItems.map((i) => {
        const exp = toDateSafe(i.expireTime);
        if (!exp) return 0;

        const diff = exp.getTime() - now;
        const itemType = normalizeType(i.type);

        const displayDiff =
          itemType === "plan"
            ? diff
            : diff - 800;

        if (displayDiff <= 0) return 0;
        return Math.ceil(displayDiff / 60000);
      })
    );
  }, [visibleItems, now]);

  const rem = formatRemaining(remainingMin);
  const type = normalizeType(first?.type);
  const showMainCard = type !== "plan" || planVisible;

  useEffect(() => {
    if (!showMainCard) {
      setCardVisible(false);
      setTimeVisible(false);
      return;
    }

    setCardVisible(false);
    setTimeVisible(false);

    const cardId = setTimeout(() => {
      setCardVisible(true);
    }, 10);

    const timeId = setTimeout(() => {
      setTimeVisible(true);
    }, 300);

    return () => {
      clearTimeout(cardId);
      clearTimeout(timeId);
    };
  }, [showMainCard, first?.activeKey]);
  

  if (!visibleItems.length && !expired) return null;
  if (!first && !expired) return null;

  const styleMap = {
    ticket: {
      icon: <Ticket size={18} />,
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-900",
      badge: "bg-gray-900"
    },
    goods: {
      icon: <Handbag size={18} />,
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-800",
      badge: "bg-amber-600"
    },
    plan: {
      icon: <Crown size={18} />,
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-900",
      badge: "bg-gray-900"
    }
  };

  let style = styleMap[type] || styleMap.ticket;

  if (type !== "plan" && remainingMin <= 5) {
    style = {
      ...style,
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-950",
      badge: "bg-amber-700"
    };
  }

  const expireTimeStr = (() => {
    const d = toDateSafe(first?.expireTime);
    if (!d) return "—";
    return `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();

return (
  <>
    {expired && (
      <ExpiredModal
        open={true}
        onClose={handleClose}
      />
    )}

    {showMainCard && (
      <div className={className}>
        <div
          className={[
            "p-5 rounded-3xl border shadow-md transition-all duration-400",
            cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
            style.bg,
            style.border,
          ].join(" ")}
        >
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="mt-1">
                <span
                  className={`inline-flex text-[10px] font-black px-3 py-1 rounded-xl text-white ${style.badge}`}
                >
                  IN USE
                </span>
              </div>

              <p className={`mt-3 text-[12px] font-bold opacity-70 ${style.text}`}>
                終了予定 {expireTimeStr}
              </p>
            </div>

            {type !== "plan" && (
              <div className="px-5 py-3 min-w-[92px] flex flex-col items-end">
                <div
                  className={[
                    `text-[42px] font-black leading-none ${style.text} transition-all duration-300`,
                    timeVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
                  ].join(" ")}
                >
                  {rem.main}
                  {rem.unit && (
                    <span className="ml-1 text-[15px] font-black">
                      {rem.unit}
                    </span>
                  )}
                </div>

                <p className={`mt-1 text-[11px] font-bold opacity-70 ${style.text}`}>
                  残り時間
                </p>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-black/5 space-y-1">
            {grouped.map((i) => (
              <div
                key={i.title}
                className="flex justify-between text-base font-bold"
              >
                <span>{i.title}</span>
                {type === "goods" && <span>×{i.quantity}</span>}
              </div>
            ))}
          </div>

          {type === "goods" && (
            <div className="mt-3 pt-3 border-t border-black/5">
              <div className="flex items-start gap-2">
                <AlertTriangle
                  size={15}
                  className="mt-[2px] text-amber-700 shrink-0"
                />
                <div>
                  <p className="text-[11px] font-black text-amber-900">
                    グッズは入場時に全数消化されます
                  </p>
                  <p className="text-[10px] font-bold text-amber-800 opacity-70">
                    お受け取り忘れにご注意ください
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
  </>
);
};

export default ActiveTimeBlock;